import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { cleanName, orNull } from '../../../common/utils/text';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useDeleteLab, useLab, useUpdateLab } from '../contexts/labs';
import { validateLab } from '../utils/validate';
import LabFields from './fields';

import '../../../common/styles/catalog.css';

function buildForm(lab) {
    return { name: lab.name || '', location: lab.location || '', status: lab.status ?? 2 };
}

export default function EditLabForm({ labId }) {
    const navigate = useNavigate();
    const { data: lab, isLoading, error: loadError } = useLab(labId);
    const { mutateAsync: updateLab, isPending: updating } = useUpdateLab(labId);
    const { mutateAsync: deleteLab, isPending: deleting } = useDeleteLab();
    const confirm = useConfirm();

    const [form, setForm]           = useState(null);
    const [seededFor, setSeededFor] = useState(null);
    const [localErrors, setLocalErrors]   = useState({});
    const [serverFields, setServerFields] = useState({});

    if (lab && seededFor !== lab.id) {
        setSeededFor(lab.id);
        setForm(buildForm(lab));
    }

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors(prev => ({ ...prev, [field]: undefined }));
            setServerFields(prev => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    if (isLoading || (!form && !loadError)) return <div className="cat-form__state">Cargando laboratorio…</div>;
    if (loadError || !lab) return <div className="cat-form__state">Laboratorio no encontrado.</div>;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateLab(form);
        if (Object.keys(errors).length) { setLocalErrors(errors); return; }
        setLocalErrors({});
        setServerFields({});

        try {
            await updateLab({ name: cleanName(form.name), location: orNull(form.location), status: form.status });
            toast.success('Laboratorio actualizado');
            navigate(`/admin/labs/${labId}`);
        } catch (err) {
            if (err?.status === 409) toast.error('Ya existe un laboratorio con ese nombre');
            else if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else toast.error(err?.message || 'No se pudo actualizar el laboratorio');
        }
    };

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Eliminar laboratorio',
            message: `¿Eliminar el laboratorio "${lab.name}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await deleteLab(labId);
            toast.success('Laboratorio eliminado');
            navigate('/admin/labs');
        } catch (err) {
            if (err?.status === 409) toast.error('No se puede eliminar: tiene registros asociados');
            else toast.error(err?.message || 'No se pudo eliminar el laboratorio');
        }
    };

    return (
        <form className="cat-form" onSubmit={handleSubmit}>
            <header className="cat-form__header">
                <Link to={`/admin/labs/${labId}`} className="cat-form__back">Volver al laboratorio</Link>
                <h1 className="cat-form__title">Editar laboratorio</h1>
                <span className="cat-form__subtitle">#{lab.id}</span>
            </header>

            <LabFields form={form} setField={setField} fieldError={fieldError} disabled={updating} showStatus />

            <footer className="cat-form__footer">
                <Link to={`/admin/labs/${labId}`} className="cat-form__cancel">Cancelar</Link>
                <Gate domain="labs" action="write">
                    <button type="button" className="cat-form__danger" onClick={handleDelete} disabled={updating || deleting}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </Gate>
                <button type="submit" className="cat-form__submit" disabled={updating || !form.name.trim()}>
                    {updating ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </footer>
        </form>
    );
}
